/**
 * SDK contract for `CategoryPageSubheaders` (ECOMM-13289 / PF-9650).
 *
 * The registry key itself needs to be added to the published
 * `@dutchiesdk/ecommerce-extensions-sdk` package's `RemoteModuleRegistry`
 * type (that package's source isn't part of this monorepo). Because
 * `RemoteModuleRegistry` is a `type` alias (not an `interface`), it can't be
 * extended here via TS declaration merging, so this file defines the entry
 * shape locally and an `ExtendedRemoteModuleRegistry` intersection type used
 * only where the loaded remote module is read (see remote-theme.tsx). Once
 * the SDK package publishes `CategoryPageSubheaders` on `RemoteModuleRegistry`
 * directly, this local shim (and the cast at its one call site) can be
 * deleted in favor of the real type.
 */

import type { ListPageEntry, RemoteModuleRegistry } from '@dutchiesdk/ecommerce-extensions-sdk';

/** Uses the same category-key contract as the SDK's existing list-page registries. */
export type CategoryPageSubheaderCategory = NonNullable<ListPageEntry['category']>;

export type CategoryPageSubheaderEntry = {
  /** Required. Matched exactly against the current category (see `findMatchingCategorySubheader`). No undefined-category wildcard/default entry is supported. */
  category: CategoryPageSubheaderCategory;
  /** Required. Rendered as a single native paragraph directly under the category H1. */
  text: string;
};

/**
 * Local stand-in for `RemoteModuleRegistry` with `CategoryPageSubheaders`
 * added. Only use this type where the loaded remote module is read.
 */
export type ExtendedRemoteModuleRegistry = RemoteModuleRegistry & {
  CategoryPageSubheaders?: CategoryPageSubheaderEntry[];
};

/**
 * Resolves the current category the same way `useProductListInjections`
 * does for `CategoryPageInterstitials`/`CategoryPageSlots`: prefer the
 * `category` route param, falling back to `slug`.
 */
export const getCategoryToMatch = (query: Record<string, unknown> | undefined): string | undefined => {
  const category = query?.category ?? query?.slug;
  return typeof category === 'string' ? category : undefined;
};

/**
 * Fails closed to `undefined` (today's H1-only layout) unless there's an
 * entry with an exact category match and a valid, non-empty, non-whitespace
 * `text`. Deliberately does not fall back to an entry with no `category`
 * (no wildcard/default entry in this version).
 */
export const findMatchingCategorySubheaderText = (
  entries: unknown,
  categoryToMatch: string | undefined
): string | undefined => {
  if (!Array.isArray(entries) || entries.length === 0 || !categoryToMatch) {
    return undefined;
  }

  // Registry values cross a remote-module runtime boundary, so validate the
  // container and each matching entry instead of trusting compile-time types.
  // Duplicate categories intentionally use the first matching entry,
  // consistent with Array.find and the SDK's ordered registry conventions.
  const match: unknown = entries.find(
    (entry: unknown) =>
      entry !== null && typeof entry === 'object' && 'category' in entry && entry.category === categoryToMatch
  );

  if (match === null || typeof match !== 'object' || !('text' in match) || typeof match.text !== 'string') {
    return undefined;
  }

  const trimmed = match.text.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};
